import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import PhoneInput from "react-phone-input-2";
import Sidebar from "@/pages/sidebar/sidebar";
import ClientMainHeader from "@/pages/header";
import { StyledInput, StyledTextInput } from "@/pages/utils/styled-input";
import { validateEmail } from "@/scripts/functions";
import { getContactById, updateContact } from "./actions";
import { MainWrapper } from "@/pages/styled";
import { FormBtnSection, FormLabel, FormSubLabel, OptionLabel } from "./styled";
import "react-phone-input-2/lib/style.css";
import { useSelector } from "react-redux";
import { Button } from "@/components/ui/button-tw";
import { toast } from "react-toastify";
import { customTimeout } from "@/utils/functions";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/providers/auth";
import { InputTags } from "@/components/form/input-tags";
import { StateSelect } from "@/components/form/state-select";

const UpdateContact = ({ match }) => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { id } = match.params;
  const { cognitoId, clientId, userRole } = useAuth();

  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [shortCut, setShortCut] = useState({
    label: "Create contact",
    value: "new-contact",
  });
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [contactId, setContactId] = useState("");
  const [businessName, setBusinessName] = useState("");
  // const [dbaName, setDbaName] = useState("");
  const [tags, setTags] = useState([]);
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipCode, setZipCode] = useState("");
  // const [note, setNote] = useState([]);
  const [validate, setValidate] = useState({
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
  });
  const [currentWidth, setCurrentWidth] = useState(0);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (id) {
      getContactInfo(id);
    }
  }, [id]);

  const validateFields = () => {
    if (firstName === "") {
      setValidate((prevState) => ({ ...prevState, firstName: false }));
    } else {
      setValidate((prevState) => ({ ...prevState, firstName: true }));
    }
    if (lastName === "") {
      setValidate((prevState) => ({ ...prevState, lastName: false }));
    } else {
      setValidate((prevState) => ({ ...prevState, lastName: true }));
    }
    if (email) {
      if (validateEmail(email.trim())) {
        setValidate((prevState) => ({ ...prevState, email: true }));
      } else {
        setValidate((prevState) => ({ ...prevState, email: false }));
      }
    } else {
      setValidate((prevState) => ({ ...prevState, email: true }));
    }

    if (phone === "") {
      setValidate((prevState) => ({ ...prevState, phone: false }));
    } else {
      if (phone?.length < 10) {
        setValidate((prevState) => ({ ...prevState, phone: false }));
      } else {
        setValidate((prevState) => ({ ...prevState, phone: true }));
      }
    }

    if (contactId) {
      if (contactId.length > 25) {
        setValidate((prevState) => ({ ...prevState, contactId: false }));
        // toast.error("Contact ID must be 25 characters or less.");
      } else if (/\s/.test(contactId)) {
        setValidate((prevState) => ({ ...prevState, contactId: false }));
        // toast.error("Contact ID cannot contain spaces.");
      } else {
        // Basic SQL injection check
        const sqlInjectionPattern =
          /(\b(SELECT|INSERT|UPDATE|DELETE|DROP|UNION|SCRIPT|--|;|\/\*|\*\/)\b)|['"]/i;
        if (sqlInjectionPattern.test(contactId)) {
          setValidate((prevState) => ({ ...prevState, contactId: false }));
          // toast.error("Invalid characters in Contact ID.");
        } else {
          setValidate((prevState) => ({ ...prevState, contactId: true }));
        }
      }
    } else {
      setValidate((prevState) => ({ ...prevState, contactId: true })); // Optional field
    }
  };

  useEffect(() => {
    if (id) {
      validateFields();
    }
  }, [firstName, lastName, email, phone, contactId]);

  const getContactInfo = async (id) => {
    await getContactById({ contactId: id, userId }).then((res) => {
      if (res && res?.length > 0) {
        setFirstName(res[0].contact_info.first_name);
        setLastName(res[0].contact_info.last_name);
        setEmail(res[0].contact_info.email) || "";
        setPhone(res[0].contact_info.phone);
        setContactId(res[0].contact_info.contact_ref_id);
        setBusinessName(res[0].contact_info.business_name);
        // setDbaName(res[0].contact_info.business_dba);
        setTags(res[0].contact_info.tags);
        setAddress(res[0].contact_info.business_address);
        setCity(res[0].contact_info.business_city);
        setState(res[0].contact_info.business_state);
        setZipCode(res[0].contact_info.business_zip);
        // setNote(res[0].contact_info.notes[0] || "");
      }
    });
  };

  const handleUpdateContact = async () => {
    const clientCognitoId = JSON.parse(localStorage.getItem("PLATFORM_ID"));
    if (!clientCognitoId) {
      throw new Error("It was not possible to identify the platform.");
    }

    const payload = {
      cognito_id: clientCognitoId,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: email.trim(),
      phone: phone,
      contact_ref_id: contactId.trim() || "",
      tags: tags,
      business_name: businessName.trim(),
      // business_dba: dbaName,
      business_address: address.trim(),
      business_city: city.trim(),
      business_state: state,
      business_zip: zipCode.trim(),
      business_country: "",
      notes: [],
      integrations: {},
    };
    if (
      validate.firstName &&
      validate.lastName &&
      validate.email &&
      validate.phone &&
      validate.contactId
    ) {
      await updateContact({ payload, contactId: id, userId })
        .then((res) => {
          if (res && res.status === "success") {
            queryClient.invalidateQueries(["contacts"]);
            toast.success("Your contact has been updated");
            setSubmitLoading(false);
            customTimeout(() => {
              history.push("/contacts");
            });
          }
        })
        .catch((err) => {
          toast.error("Failed to update contact. Please try again.");
          setSubmitLoading(false);
        });
    } else {
      toast.error("Please fill the missing information.");
      validateFields();
    }
  };

  return (
    <div>
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      <MainWrapper>
        <ClientMainHeader
          title="Contacts"
          subTitle="Update a Contact"
          shortCut={shortCut}
          setShortCut={setShortCut}
        />
        <div className="pt-24 pb-25">
          <OptionLabel>Primary Contact Information</OptionLabel>
          <div className="flex mb-20 align-items-center">
            <FormLabel>*Name</FormLabel>
            <StyledInput
              value={firstName}
              setValue={setFirstName}
              placeholder={"First Name"}
              width="244px"
              mRight="24px"
              validation={validate.firstName}
            />
            <StyledInput
              value={lastName}
              setValue={setLastName}
              placeholder={"Last Name"}
              width="244px"
              validation={validate.lastName}
            />
          </div>
          <div className="flex mb-20 align-items-center">
            <div>
              <FormLabel>*Email Address</FormLabel>
              <FormSubLabel>
                Invoices will be sent to this email address.
              </FormSubLabel>
            </div>
            <StyledInput
              value={email}
              setValue={setEmail}
              placeholder={"email"}
              width="512px"
              validation={validate.email}
            />
          </div>
          <div className="flex mb-20 align-items-center">
            <FormLabel>*Phone Number</FormLabel>
            <div style={{ width: "512px" }}>
              <PhoneInput
                placeholder="Phone number"
                country={"us"}
                onlyCountries={["us"]}
                value={phone}
                style={{ border: !validate.phone && "1px solid #ff0000" }}
                onChange={(phone) => {
                  setPhone(phone);
                }}
                disableCountryCode
              />
            </div>
          </div>
          <div className="flex mb-20 align-items-center">
            <div>
              <FormLabel>Contact ID #</FormLabel>
              <FormSubLabel>Optional reference id.</FormSubLabel>
            </div>
            <div className="flex flex-col">
              <StyledInput
                value={contactId}
                setValue={setContactId}
                width="512px"
                validation={validate.contactId}
              />
              {!validate.contactId && (
                <p className="text-sm text-red-600 mt-1">
                  {contactId.length > 25
                    ? "Contact ID must be 25 characters or less."
                    : /\s/.test(contactId)
                    ? "Contact ID cannot contain spaces."
                    : "Invalid characters in Contact ID."}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="pt-24 pb-25">
          <OptionLabel>Business Contact Information</OptionLabel>
          <div className="flex mb-20 align-items-center">
            <FormLabel>Business Name</FormLabel>
            <StyledInput
              value={businessName}
              setValue={setBusinessName}
              placeholder={"Business Name"}
              disabled
              width="512px"
            />
          </div>
          {/* <div className="flex mb-20 align-items-center">
            <FormLabel>DBA</FormLabel>
            <StyledInput
              value={dbaName}
              setValue={setDbaName}
              placeholder={"Title"}
              width="512px"
            />
          </div> */}
          <div className="flex mb-20 align-items-center">
            <FormLabel>Tags</FormLabel>
            <div>
              <InputTags
                id="tags"
                value={tags}
                onChange={setTags}
                className="w-[512px] "
                inputClassName=" focus-visible:shadow-[0_0_0_0.3rem_rgba(0,123,255,.25)] focus-visible:ring-0 focus-visible:border focus-visible:border-[#80BDFF]"
              />
            </div>
            {/* <StyledInput
              value={tag}
              setValue={(value) => {
                setTag([value]);
              }}
              placeholder={"Create a tag"}
              width="512px"
            /> */}
          </div>
          <div className="flex mb-20 align-items-center">
            <FormLabel>Physical Address</FormLabel>
            <StyledInput
              value={address}
              setValue={setAddress}
              placeholder={"Physical Address"}
              width="512px"
            />
          </div>
          <div className="flex mb-20 align-items-center">
            <FormLabel>City</FormLabel>
            <StyledInput
              value={city}
              setValue={setCity}
              placeholder={"City"}
              width="512px"
            />
          </div>
          <div className="flex mb-20 align-items-center">
            <FormLabel>State / Province</FormLabel>
            <div className="w-36 mr-4">
              <StateSelect
                value={state}
                onChange={setState}
                placeholder="State"
                className="border-gray-300 h-[42px] focus:ring-0"
              />
            </div>
            {/* <StyledInput
              value={state}
              setValue={setState}
              placeholder={"State"}
              width="148px"
              mRight="24px"
            /> */}
            <StyledInput
              value={zipCode}
              setValue={setZipCode}
              placeholder={"Zip Code"}
              width="148px"
            />
          </div>
          {/* <div className="pt-24">
            <OptionLabel>Other</OptionLabel>
            <div className="flex">
              <FormLabel>Notes</FormLabel> */}
          {/* <StyledTextInput value={note} setValue={setNote} width="512px" /> */}
          {/* </div>
          </div> */}
        </div>
        <FormBtnSection>
          <Button variant="outline" onClick={() => history.push("/contacts")}>
            Cancel
          </Button>
          <Button
            className="bg-skin-primary hover:bg-skin-primary/90 ml-2"
            onClick={() => handleUpdateContact()}
          >
            Update
            {submitLoading && (
              <CircularProgress color="inherit" size={14} className="ml-08" />
            )}
          </Button>
        </FormBtnSection>
      </MainWrapper>
    </div>
  );
};

export default UpdateContact;
